import { NgModule, ErrorHandler } from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { ToastrModule } from 'ngx-toastr'
import { PERFECT_SCROLLBAR_CONFIG, PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar'
import { CookieService } from 'ngx-cookie-service'
import { EffectsModule } from '@ngrx/effects'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'

/*
 * Platform and Environment providers/directives/pipes
 */
import { AppRoutingModule } from './app.routes'
import { AppComponent } from './app.component'
import { AppConfig } from './app.config'
import { HttpErrorInterceptor, HttpCredentialsInterceptor } from './common/http'

import { RouteAuthGuard, AuthService, NavigationService, SentryErrorHandler } from './common'

import { StoreModule } from '@ngrx/store'
import { UsersService } from './users'
import { reducers, metaReducers } from './@store'
import { environment } from '../environments/environment'
import { UserEffects } from './@store/effects/user.effects'
import { CenterEffects } from './@store/effects/center.effects'
import { FlightNetworkEffects } from './@store/effects/flight-network.effects'
import { ReleaseInfoEffects } from './@store/effects/release-info.effects'
import { NgbModule } from '@ng-bootstrap/ng-bootstrap'
import { MeasurementUnitConvertPipe, MeasurementUnitsPipe } from './common/measurement-units.pipe'

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
}

// Application wide providers
const APP_PROVIDERS = [
  AppConfig,
  MeasurementUnitsPipe,
  MeasurementUnitConvertPipe,
  RouteAuthGuard,

  NavigationService,

  AuthService,
  UsersService,
  CookieService,

  { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: HttpCredentialsInterceptor, multi: true },
  { provide: PERFECT_SCROLLBAR_CONFIG, useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG },
  { provide: ErrorHandler, useClass: SentryErrorHandler },
]

/**
 * `AppModule` is the main entry point into Angular's bootstrapping process
 */
@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent],
  /**
   * Import Angular's modules.
   */
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    FormsModule,
    HttpClientModule,
    NgbModule,
    // https://github.com/scttcper/ngx-toastr#global-options
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-left',
      preventDuplicates: true,
      disableTimeOut: false,
      timeOut: 5000,
      tapToDismiss: false,
      closeButton: true,
      iconClasses: {
        // These overrides are defined in ../styles.scss
        error: 'toast-error toast-error--exclamation-mark',
      },
    }),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
        strictStateSerializability: false,
        strictActionSerializability: false,
      },
    }),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
    }),
    EffectsModule.forRoot([UserEffects, CenterEffects, FlightNetworkEffects, ReleaseInfoEffects]),
  ],
  /**
   * Expose our Services and Providers into Angular's dependency injection.
   */
  providers: APP_PROVIDERS,
})
export class AppModule {}
